import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useAuth } from '@/context/AuthContext';
import { auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import { toast } from 'sonner';

interface AuthGuardProps {
  children?: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { user, loading } = useAuth();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Du har loggats ut');
    } catch (error) {
      console.error('Fel vid utloggning:', error);
      toast.error('Något gick fel vid utloggning.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      {/* Slim global top bar for authenticated staff */}
      <div className="bg-slate-900 text-slate-100 text-xs py-1.5 px-4 flex justify-between items-center z-[60] relative">
        <div className="flex items-center gap-2">
          <span className="font-semibold hidden sm:inline">Inloggad som:</span>
          <span className="opacity-90">{user.email}</span>
        </div>
        <button 
          onClick={handleLogout}
          className="flex items-center gap-1.5 hover:text-white hover:bg-slate-800 px-2 py-1 rounded transition-colors"
        >
          <LogOut size={14} />
          <span>Logga ut</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full bg-background relative z-0">
        {children ? children : <Outlet />}
      </div>
    </div>
  );
};

export default AuthGuard;
